import React, { useCallback, useState } from "react";
import { Alert, Col, Form, Modal, Row } from "react-bootstrap";
import { ArrowCounterclockwise } from "react-bootstrap-icons";
import { useForm } from "react-hook-form";
import { SemesterRecord, ThesisRecord, ThesisResponse, ThesisStatusRecord, ThesisTypeRecord } from "../generated/pocketbase";
import { SALUTATIONS } from "../utils/config";
import { pb } from "../utils/pocketbase";
import { Button } from "./Button";

export const UpsertThesis = (p: {
  thesis?: ThesisRecord;
  selectedThesisType: ThesisTypeRecord;
  thesisTypes: ThesisTypeRecord[];
  selectedSemester: SemesterRecord;
  semester: SemesterRecord[];
  thesisStatus: ThesisStatusRecord[];
  onClose: (saved?: boolean) => void;
}) => {
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [cancelWarning, setCancelWarning] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);

  const defaultValues = p.thesis ? {
    ...p.thesis,
    lastContactDate: p.thesis.lastContactDate?.slice(0, 10),
    presentationDate: p.thesis.presentationDate?.slice(0, 10),
    submissionDate: p.thesis.submissionDate?.slice(0, 10),
  } as ThesisResponse : {
    comments: [],
    type: p.selectedThesisType.id,
    semester: p.selectedSemester.id,
    status: p.thesisStatus[0]?.id,
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isDirty, errors, },
  } = useForm<ThesisRecord>({ defaultValues });

  const color = watch("color");

  const handleClose = useCallback(() => {
    if (!isDirty) {
      p.onClose();
    }
    setCancelWarning(true);
  }, [isDirty]);

  const onSubmit = useCallback((thesis: ThesisRecord) => {
    setErrorMessage(undefined);
    setPending(true);

    const collection = pb.collection("Thesis");

    (thesis.id ? collection.update(thesis.id, thesis) : collection.create(thesis))
      .then(() =>
        p.onClose(true)
      )
      .catch(error =>
        setErrorMessage(error.message)
      )
      .finally(() => setPending(false));
  }, []);

  return (
    <Modal show size="lg" fullscreen="lg-down" onHide={handleClose}>
      {(errorMessage || cancelWarning) && (
        <Modal.Body>
          <Alert
            variant="danger"
            show={!!errorMessage}
            dismissible
            onClose={() => setErrorMessage(undefined)}
          >
            {errorMessage}
          </Alert>
          <Alert
            variant="danger"
            show={cancelWarning}
            dismissible
            onClose={() => setCancelWarning(false)}
          >
            <Alert.Heading>Are you sure?</Alert.Heading>
            <p>Your unsaved changes will be lost when you close the dialog.</p>
            <Button
              variant="danger"
              onClick={() => p.onClose()}
            >
              Close anyway
            </Button>
          </Alert>
        </Modal.Body>
      )}
      {deleteMode && p.thesis ? (
        <DeleteThesis
          thesis={p.thesis}
          pending={pending}
          onClose={p.onClose}
          setErrorMessage={setErrorMessage}
          setPending={setPending}
          onCancel={() => setDeleteMode(false)}
        />
      ) : (
        <>
          <Modal.Header closeButton>
            <Modal.Title>{p.thesis ? "Update Entry" : "New Entry"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col xs="3">
                <Form.Group className="mb-3" controlId="salutation">
                  <Form.Label>Salutation</Form.Label>
                  <Form.Select {...register("salutation")}>
                    {SALUTATIONS.map(s =>
                      <option key={s}>{s}</option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3" controlId="firstName">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    isInvalid={!!errors.name}
                    {...register("name", { required: true })}
                  />
                </Form.Group>
              </Col>
              <Col xs="3">
                <Form.Group className="mb-3" controlId="semester">
                  <Form.Label>Semester</Form.Label>
                  <Form.Select {...register("semester")}>
                    {p.semester.map(s =>
                      <option key={s.id} value={s.id}>{s.name}</option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs="3">
                <Form.Group className="mb-3" controlId="thesisType">
                  <Form.Label>Type</Form.Label>
                  <Form.Select {...register("type")}>
                    {p.thesisTypes.map(type =>
                      <option key={type.id} value={type.id}>{type.name}</option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3" controlId="topic">
                  <Form.Label>Topic</Form.Label>
                  <Form.Control type="text" {...register("topic")} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs="3">
                <Form.Group className="mb-3" controlId="supervisorName">
                  <Form.Label>Who looks after?</Form.Label>
                  <Form.Control
                    type="text"
                    isInvalid={!!errors.supervisorName}
                    {...register("supervisorName", { required: true })}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3" controlId="relatedProject">
                  <Form.Label>Helps with?</Form.Label>
                  <Form.Control type="text" {...register("relatedProject")} />
                </Form.Group>
              </Col>
              <Col xs="3">
                <Form.Group controlId="color">
                  <Form.Label>Color</Form.Label>
                  <div className="mb-3 d-flex flex-row ">
                    <Form.Control
                      className="me-2"
                      type="color"
                      defaultValue="#ffffff"
                      title="Choose your color"
                      {...register("color")}
                    />
                    <Button
                      variant={`outline-secondary ${color === "#ffffff" ? "invisible" : "visible"}`}
                      icon={ArrowCounterclockwise}
                      onClick={() => setValue("color", "#ffffff", { shouldDirty: true })}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Body>
            <Row>
              <Col xs="3">
                <Form.Group className="mb-3" controlId="thesisStatus">
                  <Form.Label>Status</Form.Label>
                  <Form.Select {...register("status")}>
                    {p.thesisStatus.map(s =>
                      <option key={s.id} value={s.id}>{s.name}</option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs="3">
                <Form.Group className="mb-3" controlId="lastContactDate">
                  <Form.Label>Last meeting / email</Form.Label>
                  <Form.Control type="date" {...register("lastContactDate")} />
                </Form.Group>
              </Col>
              <Col xs="3">
                <Form.Group className="mb-3" controlId="submissionDate">
                  <Form.Label>Submission due</Form.Label>
                  <Form.Control type="date" {...register("submissionDate")} />
                </Form.Group>
              </Col>
              <Col xs="3">
                <Form.Group className="mb-3" controlId="presentationDate">
                  <Form.Label>Presentation Date</Form.Label>
                  <Form.Control type="date" {...register("presentationDate")} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3" controlId="memo">
              <Form.Label>Memo</Form.Label>
              <Form.Control as="textarea" rows={3} {...register("memo")} />
            </Form.Group>
          </Modal.Body>
        </>
      )}

      <Modal.Footer className="justify-content-between flex-row-reverse">
        <div className="d-flex flex-row">
          <Button
            className="me-2"
            variant="outline-secondary"
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            variant="outline-primary"
            onClick={handleSubmit(onSubmit)}
            spinner={pending}
            disabled={!isDirty}
          >
            {p.thesis ? "Save Changes" : "Create"}
          </Button>
        </div>
        {p.thesis && (
          <Button
            variant="outline-danger"
            disabled={deleteMode}
            onClick={() => setDeleteMode(true)}
          >
            Delete Thesis
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

const DeleteThesis = (p: {
  thesis: ThesisRecord;
  pending: boolean;
  setErrorMessage: (error?: string) => void;
  setPending: (pending: boolean) => void;
  onClose: (saved?: boolean) => void;
  onCancel: () => void;
}) => {
  const [safeWord, setSafeWord] = useState<string>("");

  const onDelete = useCallback(() => {
    p.setPending(true);
    pb.collection("Thesis")
      .delete(p.thesis.id)
      .then(() => p.onClose(true))
      .catch(error => p.setErrorMessage(error.message))
      .finally(() => p.setPending(false));
  }, [p.thesis]);

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Delete Thesis of {p.thesis.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3" controlId="firstName">
          <Form.Label>If you are certain that you want to remove this thesis then please type "<pre className="d-inline text-danger">{p.thesis.name.trim()}</pre>":</Form.Label>
          <Form.Control
            type="text"
            isInvalid={safeWord !== p.thesis.name.trim()}
            isValid={safeWord === p.thesis.name.trim()}
            onChange={({ target }) => setSafeWord(target.value)}
          />
        </Form.Group>
        <div className="d-flex justify-content-end">
          <Button
            variant="outline-secondary"
            className="me-2"
            onClick={p.onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={safeWord !== p.thesis.name.trim()}
            onClick={onDelete}
            spinner={p.pending}
          >
            Delete
          </Button>
        </div>
      </Modal.Body>
    </>
  );
}
